// src/components/MapChart.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  MapContainer,
  ZoomControl,
  TileLayer,
  Marker,
  Popup,
  useMap,
  Tooltip,
  CircleMarker,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
// import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
// import markerIcon from "leaflet/dist/images/marker-icon.png";
// import markerShadow from "leaflet/dist/images/marker-shadow.png";
import ShipDataGrid from "./ShipDataGrid";

const baseShipImgUrl = `${process.env.PUBLIC_URL}/dist/img/shipmarker.png`;

// Rotated ship icon (used when heading is available AND not arrived)
const makeRotatedShipIcon = (headingDeg) => {
  const heading = Number(headingDeg);
  const finalHeading = Number.isFinite(heading) ? heading : 0;

  return L.divIcon({
    className: "ship-marker-rotated",
    html: `
      <div
        style="
          display: flex;
          align-items: center;
          justify-content: center;
          transform: rotate(${finalHeading}deg);
          transform-origin: 50% 50%;
          pointer-events: auto;
        "
      >
        <img
          src="${baseShipImgUrl}"
          style="width: 70px; height: 70px; display: block;"
        />
      </div>
    `,
    iconSize: [70, 70],
    iconAnchor: [30, 30],
    tooltipAnchor: [30, 0],
  });
};

// Default blue Leaflet marker (used when arrival_status === true)
// const defaultBlueIcon = L.icon({
//   iconUrl: markerIcon,
//   iconRetinaUrl: markerIcon2x,
//   shadowUrl: markerShadow,
//   iconSize: [25, 41],
//   iconAnchor: [12, 41],
//   popupAnchor: [1, -34],
//   tooltipAnchor: [16, -28],
// });

// Invisible icon (used when heading is missing → we show only a dark blue circle)
const invisibleIcon = L.divIcon({
  className: "invisible-icon",
  html: '<div style="width:1px;height:1px;background:transparent;"></div>',
  iconSize: [1, 1],
  iconAnchor: [0, 0],
  tooltipAnchor: [0, 0],
});

/* Helper component: fit map bounds to markers whenever they change */
function FitBounds({ markers, fallback = [6.29, -55.04] }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const pts = (markers || [])
      .map((m) => {
        const lat = Number(m.lat);
        const lng = Number(m.long);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return [lat, lng];
      })
      .filter(Boolean);

    if (pts.length === 0) {
      map.setView(fallback, 2);
      return;
    }

    if (pts.length === 1) {
      map.setView(pts[0], 6, { animate: true });
      return;
    }

    try {
      const bounds = L.latLngBounds(pts);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8, animate: true });
    } catch (err) {
      // silent
    }
  }, [map, markers, fallback]);

  return null;
}

/**
 * MapChart
 * props:
 *  - markers: [{ id, subject, content, lat, long, ship_name, heading_deg, arrival_status }]
 *  - allMails: [{ ship_name, attachments, received, subject, ... }]
 */
const MapChart = ({
  markers = [],
  allMails = [],
  initialCenter = [6.29, -55.04],
  initialZoom = 2,
  onMarkerClick,
}) => {
  const [showShipDataGrid, setShowShipDataGrid] = useState(false);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const timeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Organize mails by ship for the grid
  const shipsData = useMemo(() => {
    if (!allMails || allMails.length === 0) return [];

    const shipMap = new Map();

    allMails.forEach((mail) => {
      const shipName = mail.ship_name || "Unknown Ship";
      if (!shipMap.has(shipName)) {
        shipMap.set(shipName, {
          ship_name: shipName,
          mails: [],
        });
      }
      shipMap.get(shipName).mails.push({
        subject: mail.subject,
        received: mail.received,
        attachments: mail.attachments || [],
      });
    });

    return Array.from(shipMap.values());
  }, [allMails]);

  const validMarkers = useMemo(
    () =>
      (markers || [])
        .map((m, idx) => {
          const lat = Number(m.lat);
          const lng = Number(m.long);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return {
            id: m.id ?? `${idx}-${lat}-${lng}`,
            lat,
            long: lng,
            subject: m.subject ?? "",
            content: m.content ?? m.mailContent ?? "",
            ship_name: m.ship_name ?? "Unknown Ship",
            raw: m,
          };
        })
        .filter(Boolean),
    [markers]
  );

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* Button to show ship data grid */}
      {shipsData.length > 0 && (
        <button
          onClick={() => {
            if (showShipDataGrid) {
              // Hide immediately
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setShowShipDataGrid(false);
              setIsLoadingGraph(false);
            } else {
              // Show with delay
              setIsLoadingGraph(true);
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(() => {
                setShowShipDataGrid(true);
                setIsLoadingGraph(false);
                timeoutRef.current = null;
              }, 200); // .2 seconds delay
            }
          }}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 1000,
            padding: "8px 16px",
            background: showShipDataGrid ? "#dc3545" : "DarkGreen",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            opacity: isLoadingGraph ? 0.7 : 1,
          }}
          disabled={isLoadingGraph}
          onMouseEnter={(e) => {
            if (!isLoadingGraph) {
              e.currentTarget.style.opacity = "0.9";
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoadingGraph) {
              e.currentTarget.style.opacity = "1";
            }
          }}
        >
          {isLoadingGraph ? "Loading..." : showShipDataGrid ? "Hide Graph" : "Show Graph"}
        </button>
      )}

      {/* Loading indicator */}
      {isLoadingGraph && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            background: "white",
            borderRadius: "8px",
            padding: "20px 30px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            className="spinner-border spinner-border-sm"
            role="status"
            style={{ width: "20px", height: "20px", borderWidth: "2px" }}
          >
            <span className="sr-only">Loading...</span>
          </div>
          <span style={{ fontSize: "14px", fontWeight: "500" }}>Loading graphs...</span>
        </div>
      )}

      {/* Ship Data Grid Overlay */}
      {showShipDataGrid && shipsData.length > 0 && (
        <ShipDataGrid
          shipsData={shipsData}
          onClose={() => {
            setShowShipDataGrid(false);
            setIsLoadingGraph(false);
          }}
        />
      )}

      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        zoomControl={false}
        scrollWheelZoom
      >
        <ZoomControl position="bottomright" />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds markers={validMarkers} />

        {validMarkers.map((pt, idx) => {
          const headingVal = pt.raw?.heading_deg;
          const hasHeading = Number.isFinite(Number(headingVal));
          const isArrival = pt.raw?.arrival_status === true;

          // Decide which icon to use
          let iconToUse;
          if (isArrival) {
            // arrival_status == true → blue marker
            iconToUse = invisibleIcon;
          } else if (hasHeading) {
            // normal ship with heading → rotated ship icon
            iconToUse = makeRotatedShipIcon(headingVal);
          } else {
            // heading missing → only dark blue circle (icon invisible)
            iconToUse = invisibleIcon;
          }

          return (
            <Marker
              key={pt.id}
              position={[pt.lat, pt.long]}
              icon={iconToUse}
              eventHandlers={
                onMarkerClick
                  ? {
                      click: () => onMarkerClick(pt.raw),
                    }
                  : undefined
              }
            >
              {/* ARRIVAL: orange circle around blue marker, no ship */}
              {isArrival && (
                <CircleMarker
                  center={[pt.lat, pt.long]}
                  radius={10}
                  pathOptions={{
                    color: "orange",
                    fillColor: "orange",
                    fillOpacity: 0.7,
                  }}
                />
              )}

              {/* HEADING MISSING: dark blue circle at position */}
              {!isArrival && !hasHeading && (
                <CircleMarker
                  center={[pt.lat, pt.long]}
                  radius={10}
                  pathOptions={{
                    color: "orange",
                    fillColor: "orange",
                    fillOpacity: 0.7,
                  }}
                />
              )}

              <Tooltip permanent direction="right" offset={[0, 0]}>
                <div
                  style={{
                    background: "white",
                    border: "2px solid #3388ff",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                    color: "#333",
                    margin: 0,
                  }}
                >
                  {(() => {
                    // Short ship names
                    const shortName =
                      {
                        Albemarle: "Albe",
                        Barrington: "Bari",
                        Charles: "Char",
                        Duncan: "Dunc",
                        Hood: "Hood",
                      }[pt.ship_name] || pt.ship_name;

                    // FORMAT RECEIVED DATE → DD/HHMM
                    let recv = "";
                    const r = pt.raw?.received || pt.raw?.Headers?.Date;
                    if (r) {
                      const d = new Date(r);
                      if (!isNaN(d.getTime())) {
                        const DD = String(d.getDate()).padStart(2, "0");
                        const HH = String(d.getHours()).padStart(2, "0");
                        const MM = String(d.getMinutes()).padStart(2, "0");
                        recv = `${DD}/${HH}${MM}`;
                      }
                    }

                    // FORMAT SPEED → 17.2 kts
                    let speed = "";
                    const numericSpeed = Number(pt.raw?.speed_kts);
                     if (!pt.raw?.arrival_status && Number.isFinite(numericSpeed)) {
                      speed = `${numericSpeed.toFixed(1)} kts`;
                    }

                    return [shortName, recv, speed].filter(Boolean).join(" ");
                  })()}
                </div>
              </Tooltip>

              <Popup>
                <div style={{ minWidth: 220 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    {pt.ship_name}
                  </div>
                  <div style={{ marginBottom: 6, fontSize: 13 }}>
                    <strong>Heading:</strong>{" "} {Number.isFinite(Number(pt.raw?.heading_deg))
                      ? `${Number(pt.raw.heading_deg).toFixed(0)}°`
                      : "NA"}
                    <br />
                    <strong>Speed:</strong> {pt.raw?.speed_kts} kts
                    <br />
                    <strong>Latitude:</strong> {pt.lat.toFixed(6)}°
                    <br />
                    <strong>Longitude:</strong> {pt.long.toFixed(6)}°
                   
                  </div>
                  <hr style={{ margin: "8px 0" }} />
                  <div
                    style={{ fontWeight: 500, marginBottom: 4, fontSize: 12 }}
                  >
                    {pt.subject || "No subject"}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      whiteSpace: "pre-wrap",
                      fontSize: 11,
                      color: "#666",
                    }}
                  >
                    {pt.content
                      ? pt.content.substring(0, 150) +
                        (pt.content.length > 150 ? "..." : "")
                      : "(no content)"}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default MapChart;
